<script lang="ts">
  import { servers } from '$lib/store'

  import * as Empty from "$lib/components/ui/empty/index.js";
  import { Button } from "$lib/components/ui/button/index.js";
  import Library from "@lucide/svelte/icons/library";
  import Server from "@lucide/svelte/icons/server";
  import HomeBookshelfSection from '$lib/components/nuread/HomeBookshelfSection.svelte'
  import ResumeHero from '$lib/components/nuread/ResumeHero.svelte'
  import { getSeriesGlobal } from '$lib/apis/library/api'
  import { seriesDataEpoch } from '$lib/operations.svelte'
  import { claimServerModal } from '$lib/claim-server.svelte'
  import { filterNsfwSeries, prefs } from '$lib/preferences.svelte'
  let serverid

  let series = $state({
    "added": [],
    "continue": [],
    "score": [],
  })
  let loading = $state(true)
  let seriesLoading = $state(true)

  $effect(() => {
    void seriesDataEpoch.value
    const list = Object.values($servers)
    if (!list.length) {
      seriesLoading = false
      loading = false
      return
    }

    loading = false
    seriesLoading = true
    let cancelled = false
    const ac = new AbortController()

    series.added = []
    series.continue = []

    async function loadSort(sort: 'added' | 'continue') {
      for await (const chunk of getSeriesGlobal($servers as any, 'all', sort, {
        signal: ac.signal,
      })) {
        if (cancelled) return
        series[sort] = [...series[sort], ...chunk]
        seriesLoading = false
      }
    }

    Promise.all([loadSort('added'), loadSort('continue')]).finally(() => {
      if (!cancelled) seriesLoading = false
    })

    return () => {
      cancelled = true
      ac.abort()
    }
  })

  const isEmpty = $derived(Object.values($servers).length == 0)
  const continueSeries = $derived.by(() => {
    void prefs.displayNsfw
    return filterNsfwSeries(series['continue'])
  })
  const addedSeries = $derived.by(() => {
    void prefs.displayNsfw
    return filterNsfwSeries(series['added'])
  })
  const resumeSeries = $derived(continueSeries[0])

</script>

{#if loading}
  <div
    class="series-loader flex flex-col items-center justify-center gap-5"
    style={`height: 100dvh;`}
    role="status"
    aria-label="Loading series"
  >
    <div class="series-loader__orb">
      <span class="series-loader__ring series-loader__ring--outer"></span>
      <span class="series-loader__ring series-loader__ring--mid"></span>
      <span class="series-loader__ring series-loader__ring--inner"></span>
      <span class="series-loader__glow"></span>
      <span class="series-loader__icon">
        <Server
          class="size-8 text-latte-green dark:text-macchiato-green"
          strokeWidth={1.75}
        />
      </span>
    </div>
    <p
      class="series-loader__label text-sm tracking-wide
        text-latte-text/70 dark:text-macchiato-text/70"
    >
      Loading servers…
    </p>
  </div>
{:else if isEmpty}
<section
class="no-libs relative isolate flex min-h-[calc(100dvh-5.5rem)] w-full flex-col items-center justify-center overflow-x-clip px-6 py-16 text-center"
aria-labelledby="no-libs-title"
>
<div
  class="no-libs__glow pointer-events-none absolute left-1/2 top-[42%] size-[min(36rem,90vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-green)_22%,transparent),transparent_68%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-green)_18%,transparent),transparent_68%)]"
  aria-hidden="true"
></div>
<div
  class="no-libs__glow-2 pointer-events-none absolute left-[max(0%,12%)] top-[18%] size-56 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-mauve)_16%,transparent),transparent_70%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-mauve)_12%,transparent),transparent_70%)]"
  aria-hidden="true"
></div>
<div
  class="no-libs__glow-3 pointer-events-none absolute bottom-[14%] right-[max(0%,8%)] size-48 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-peach)_14%,transparent),transparent_70%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-peach)_10%,transparent),transparent_70%)]"
  aria-hidden="true"
></div>

<div class="no-libs__stack relative z-[1] mx-auto flex w-full max-w-3xl flex-col items-center">
  <div class="no-libs__mark relative select-none" aria-hidden="true">
    <div
      class="no-libs__badge relative flex size-[clamp(5.5rem,18vw,8.5rem)] items-center justify-center rounded-[1.75rem] bg-latte-mantle/80 text-latte-green shadow-sm ring-1 ring-latte-surface1/60 backdrop-blur-sm sm:rounded-[2rem] dark:bg-macchiato-mantle/80 dark:text-macchiato-green dark:ring-macchiato-surface1/60"
    >
      <Server class="size-[42%] opacity-90" strokeWidth={1.75} />
      <span
        class="no-libs__zero absolute right-4 top-3.5 block font-semibold leading-none tracking-tighter text-latte-text sm:right-5 sm:top-4 dark:text-macchiato-text"
        style="font-size: clamp(1.35rem, 4.5vw, 1.85rem);"
      >
        0
      </span>
    </div>
  </div>

  <h1
    id="no-libs-title"
    class="no-libs__title mt-6 text-2xl font-semibold tracking-tight text-latte-text sm:text-3xl dark:text-macchiato-text"
  >
    You've got no servers
  </h1>

  <p
    class="no-libs__copy mt-3 max-w-md text-sm leading-relaxed text-latte-text/70 sm:text-base dark:text-macchiato-text/70"
  >
    You don't have access to any server yet. <br><br> Get started by creating and claiming your server, or get invited on an existing server.  
  </p>

  <div class="no-libs__actions mt-9 flex flex-wrap items-center justify-center gap-3">
    <Button
      class="h-11 gap-2 bg-latte-green px-5 font-semibold text-latte-base hover:bg-latte-green/90 dark:bg-macchiato-green dark:text-macchiato-base dark:hover:bg-macchiato-green/90"
      onclick={() => (claimServerModal.open = true)}
    >
      <!-- <Server class="size-4" strokeWidth={2.5} /> -->
      Claim a server
    </Button>
  </div>
</div>
</section>
{:else}
  <ResumeHero series={resumeSeries} />
  <HomeBookshelfSection
    {serverid}
    series={continueSeries}
    header="Continue Reading"
    loading={seriesLoading && continueSeries.length === 0}
    showLibraryName
    showServerName
    accent="green"
  />
  <HomeBookshelfSection
    {serverid}
    series={addedSeries}
    header="Recently Added"
    loading={seriesLoading && addedSeries.length === 0}
    showLibraryName
    showServerName
    accent="sky"
  />
{/if}



<style>
  .no-libs__glow {
    animation: no-libs-breathe 7s ease-in-out infinite;
  }

  .no-libs__glow-2 {
    animation: no-libs-drift 11s ease-in-out infinite;
  }

  .no-libs__glow-3 {
    animation: no-libs-drift 13s ease-in-out infinite reverse;
  }

  .no-libs__stack {
    animation: no-libs-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .no-libs__mark {
    animation: no-libs-float 5.5s ease-in-out infinite;
  }

  .no-libs__zero {
    animation: no-libs-wiggle 2.3s ease-in-out infinite;
    transform-origin: 50% 100%;
  }

  .no-libs__title,
  .no-libs__copy,
  .no-libs__actions {
    animation: no-libs-rise 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .no-libs__title {
    animation-delay: 0.06s;
  }

  .no-libs__copy {
    animation-delay: 0.12s;
  }

  .no-libs__actions {
    animation-delay: 0.18s;
  }

  @keyframes no-libs-rise {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes no-libs-float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  @keyframes no-libs-breathe {
    0%,
    100% {
      opacity: 0.85;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.08);
    }
  }

  @keyframes no-libs-drift {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(12px, -10px);
    }
  }

  @keyframes no-libs-wiggle {
    0%,
    100% {
      transform: rotate(-2deg);
    }
    50% {
      transform: rotate(2.5deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .no-libs__glow,
    .no-libs__glow-2,
    .no-libs__glow-3,
    .no-libs__stack,
    .no-libs__mark,
    .no-libs__zero,
    .no-libs__title,
    .no-libs__copy,
    .no-libs__actions {
      animation: none;
    }
  }

  .series-loader__orb {
    position: relative;
    display: grid;
    place-items: center;
    width: 6.5rem;
    height: 6.5rem;
  }

  .series-loader__ring {
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    border: 2.5px solid transparent;
    pointer-events: none;
  }

  .series-loader__ring--outer {
    border-top-color: var(--color-latte-green);
    border-right-color: color-mix(in oklab, var(--color-latte-green) 35%, transparent);
    animation: series-spin 1.4s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
  }

  .series-loader__ring--mid {
    inset: 0.7rem;
    border-bottom-color: color-mix(in oklab, var(--color-latte-green) 55%, var(--color-latte-surface1));
    border-left-color: color-mix(in oklab, var(--color-latte-surface1) 50%, transparent);
    animation: series-spin 1.85s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite reverse;
  }

  .series-loader__ring--inner {
    inset: 1.4rem;
    border-top-color: color-mix(in oklab, var(--color-latte-green) 45%, transparent);
    border-left-color: transparent;
    animation: series-spin 1.1s linear infinite;
  }

  :global(.dark) .series-loader__ring--outer {
    border-top-color: var(--color-macchiato-green);
    border-right-color: color-mix(in oklab, var(--color-macchiato-green) 35%, transparent);
  }

  :global(.dark) .series-loader__ring--mid {
    border-bottom-color: color-mix(in oklab, var(--color-macchiato-green) 55%, var(--color-macchiato-surface1));
    border-left-color: color-mix(in oklab, var(--color-macchiato-surface1) 50%, transparent);
  }

  :global(.dark) .series-loader__ring--inner {
    border-top-color: color-mix(in oklab, var(--color-macchiato-green) 45%, transparent);
    border-left-color: transparent;
  }

  .series-loader__glow {
    position: absolute;
    inset: 1.7rem;
    border-radius: 9999px;
    background: radial-gradient(
      circle,
      color-mix(in oklab, var(--color-latte-green) 28%, transparent) 0%,
      transparent 70%
    );
    animation: series-pulse 1.8s ease-in-out infinite;
  }

  :global(.dark) .series-loader__glow {
    background: radial-gradient(
      circle,
      color-mix(in oklab, var(--color-macchiato-green) 30%, transparent) 0%,
      transparent 70%
    );
  }

  .series-loader__icon {
    position: relative;
    z-index: 1;
    display: grid;
    place-items: center;
    animation: series-bob 1.8s ease-in-out infinite;
  }

  .series-loader__label {
    animation: series-fade 1.8s ease-in-out infinite;
  }

  @keyframes series-spin {
    to { transform: rotate(360deg); }
  }

  @keyframes series-pulse {
    0%, 100% { opacity: 0.35; transform: scale(0.92); }
    50% { opacity: 0.85; transform: scale(1.05); }
  }

  @keyframes series-bob {
    0%, 100% { transform: translateY(0); opacity: 0.85; }
    50% { transform: translateY(-2px); opacity: 1; }
  }

  @keyframes series-fade {
    0%, 100% { opacity: 0.55; }
    50% { opacity: 1; }
  }
</style>
